import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Book, Plus, Search, Trash2, Edit3, Save, X, Activity, LogIn, LogOut, Upload, RotateCcw, Camera, FileText } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const OWNER_EMAIL = "eduardo@grabarte360.com";

function formatDT(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function formatDateOnly(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-ES", { weekday: "short", day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
}

const ACTION_LABEL = {
  login: { l: "Inicio de sesión", icon: LogIn, color: "text-emerald-700" },
  logout: { l: "Cierre de sesión", icon: LogOut, color: "text-[var(--ink-muted)]" },
  login_denied: { l: "Login denegado", icon: LogIn, color: "text-rose-700" },
  import_carla_post: { l: "Import Excel Carla (gastos)", icon: Upload, color: "text-blue-700" },
  import_facturas_post: { l: "Import Facturas", icon: Upload, color: "text-blue-700" },
  reset_semanal_post: { l: "Reset semanal", icon: RotateCcw, color: "text-orange-700" },
  wizard_start_post: { l: "Wizard iniciado", icon: FileText, color: "text-purple-700" },
  wizard_finalize_post: { l: "Wizard finalizado", icon: FileText, color: "text-purple-700" },
  ocr_banco_post: { l: "OCR banco (foto saldo)", icon: Camera, color: "text-teal-700" },
  factura_edit_put: { l: "Factura modificada", icon: Edit3, color: "text-[var(--ink)]" },
  factura_edit_delete: { l: "Factura borrada", icon: Trash2, color: "text-rose-700" },
  banco_edit_put: { l: "Saldo banco editado", icon: Edit3, color: "text-[var(--ink)]" },
  gastos_fijos_edit_put: { l: "Gasto fijo editado", icon: Edit3, color: "text-[var(--ink)]" },
  gastos_fijos_edit_post: { l: "Gasto fijo creado", icon: Plus, color: "text-[var(--ink)]" },
  cobro_edit_put: { l: "Cobro editado", icon: Edit3, color: "text-[var(--ink)]" },
  cobro_edit_delete: { l: "Cobro borrado", icon: Trash2, color: "text-rose-700" },
};

export default function Bitacora() {
  const { user } = useAuth();
  const isOwner = (user?.email || "").toLowerCase() === OWNER_EMAIL;
  const [tab, setTab] = useState("notas");

  if (!isOwner) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="bg-white border border-[var(--border)] rounded-lg p-8 text-center">
          <Book className="mx-auto mb-3 text-[var(--ink-muted)]" size={32} />
          <h1 className="font-display text-2xl font-black mb-2">Bitácora privada</h1>
          <p className="text-sm text-[var(--ink-muted)]">Esta sección es exclusiva del propietario.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="bitacora-page">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="font-mono-num text-[10px] uppercase tracking-widest text-[var(--ink-muted)]">Privado</p>
          <h1 className="font-display text-3xl font-black tracking-tight">Bitácora</h1>
          <p className="text-sm text-[var(--ink-muted)] mt-1">Tus notas, reflexiones y decisiones. Actividad de usuarios auditada.</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[var(--border)] mb-6">
        {[{v:"notas", l:"Notas privadas", icon: Book}, {v:"actividad", l:"Actividad usuarios", icon: Activity}].map(o => {
          const Icon = o.icon;
          const active = tab === o.v;
          return (
            <button
              key={o.v}
              data-testid={`bitacora-tab-${o.v}`}
              onClick={() => setTab(o.v)}
              className={`px-4 py-2.5 text-sm inline-flex items-center gap-2 border-b-2 -mb-px ${active ? "border-black text-[var(--ink)] font-medium" : "border-transparent text-[var(--ink-muted)] hover:text-[var(--ink)]"}`}
            >
              <Icon size={14} /> {o.l}
            </button>
          );
        })}
      </div>

      {tab === "notas" ? <NotasTab /> : <ActividadTab />}
    </div>
  );
}

function NotasTab() {
  const [entries, setEntries] = useState([]);
  const [q, setQ] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [editing, setEditing] = useState(null); // null | {id?, title, content, tags}
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (tagFilter) params.set("tag", tagFilter);
    api.get(`/bitacora?${params.toString()}`)
      .then(r => setEntries(r.data))
      .catch(err => toast.error(err?.response?.data?.detail || "Error"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tagFilter]);

  const allTags = useMemo(() => {
    const s = new Set();
    entries.forEach(e => (e.tags || []).forEach(t => s.add(t)));
    return Array.from(s).sort();
  }, [entries]);

  const save = async () => {
    if (!editing?.content?.trim()) { toast.error("Escribe algo en el contenido"); return; }
    const tags = (editing.tagsInput || "").split(",").map(s => s.trim()).filter(Boolean);
    const payload = { title: editing.title || "", content: editing.content, tags };
    try {
      if (editing.id) {
        await api.put(`/bitacora/${editing.id}`, payload);
        toast.success("Actualizada");
      } else {
        await api.post("/bitacora", payload);
        toast.success("Entrada guardada");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Borrar esta entrada?")) return;
    await api.delete(`/bitacora/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-muted)]" />
          <input
            data-testid="bitacora-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Buscar en notas..."
            className="w-full pl-9 pr-3 py-2 border border-[var(--border)] rounded-md text-sm bg-white"
          />
        </div>
        {allTags.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              onClick={() => setTagFilter("")}
              className={`text-xs px-2.5 py-1 rounded-full border ${!tagFilter ? "bg-black text-white border-black" : "bg-white border-[var(--border)]"}`}
            >Todas</button>
            {allTags.map(t => (
              <button
                key={t}
                data-testid={`tag-filter-${t}`}
                onClick={() => setTagFilter(t)}
                className={`text-xs px-2.5 py-1 rounded-full border ${tagFilter === t ? "bg-black text-white border-black" : "bg-white border-[var(--border)]"}`}
              >#{t}</button>
            ))}
          </div>
        )}
        <button
          data-testid="bitacora-new"
          onClick={() => setEditing({ title: "", content: "", tagsInput: "" })}
          className="ml-auto bg-black text-white text-sm px-4 py-2 rounded inline-flex items-center gap-2 hover:opacity-90"
        >
          <Plus size={14} /> Nueva entrada
        </button>
      </div>

      {editing && (
        <div className="bg-white border border-[var(--border)] rounded-lg p-4 mb-4">
          <input
            data-testid="bitacora-title"
            placeholder="Título (opcional)"
            value={editing.title}
            onChange={(e) => setEditing({...editing, title: e.target.value})}
            className="w-full border-b border-[var(--border)] pb-2 mb-3 font-display text-xl font-black focus:outline-none focus:border-black"
          />
          <textarea
            data-testid="bitacora-content"
            placeholder="Escribe tu nota (soporta markdown)..."
            value={editing.content}
            onChange={(e) => setEditing({...editing, content: e.target.value})}
            rows={10}
            className="w-full border border-[var(--border)] rounded p-3 text-sm font-mono focus:outline-none focus:border-black"
          />
          <div className="flex items-center gap-3 mt-3">
            <input
              data-testid="bitacora-tags"
              placeholder="Tags separadas por coma: reflexión, decisión, reunión"
              value={editing.tagsInput}
              onChange={(e) => setEditing({...editing, tagsInput: e.target.value})}
              className="flex-1 border border-[var(--border)] rounded px-3 py-2 text-sm"
            />
            <button data-testid="bitacora-save" onClick={save} className="bg-black text-white text-sm px-4 py-2 rounded inline-flex items-center gap-2 hover:opacity-90">
              <Save size={14} /> Guardar
            </button>
            <button onClick={() => setEditing(null)} className="text-sm px-3 py-2 border border-[var(--border)] rounded inline-flex items-center gap-1.5 hover:bg-[var(--hover)]">
              <X size={14} /> Cancelar
            </button>
          </div>
        </div>
      )}

      {loading && <div className="text-center text-sm text-[var(--ink-muted)] py-8">Cargando…</div>}
      {!loading && entries.length === 0 && !editing && (
        <div className="text-center text-sm text-[var(--ink-muted)] py-8">No hay entradas todavía. Crea la primera.</div>
      )}

      <div className="space-y-3">
        {entries.map(e => (
          <div key={e.id} className="bg-white border border-[var(--border)] rounded-lg p-4 hover:shadow-sm" data-testid={`bitacora-entry-${e.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-xs text-[var(--ink-muted)] font-mono-num mb-1">
                  {formatDateOnly(e.created_at)}
                  {e.updated_at && e.updated_at !== e.created_at && (
                    <span className="italic">· editada {formatDT(e.updated_at)}</span>
                  )}
                </div>
                {e.title && <h3 className="font-display text-xl font-black leading-tight mb-2">{e.title}</h3>}
                <div className="prose prose-sm max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{e.content}</ReactMarkdown>
                </div>
                {e.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {e.tags.map(t => <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-[var(--bg)] border border-[var(--border)] text-[var(--ink-muted)]">#{t}</span>)}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button data-testid={`edit-${e.id}`} onClick={() => setEditing({ id: e.id, title: e.title, content: e.content, tagsInput: (e.tags||[]).join(", ") })} className="p-1.5 text-[var(--ink-muted)] hover:text-[var(--ink)] rounded hover:bg-[var(--hover)]">
                  <Edit3 size={14} />
                </button>
                <button data-testid={`delete-${e.id}`} onClick={() => remove(e.id)} className="p-1.5 text-[var(--ink-muted)] hover:text-rose-600 rounded hover:bg-[var(--hover)]">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ActividadTab() {
  const [data, setData] = useState({ events: [] });
  const [loading, setLoading] = useState(true);
  const [emailFilter, setEmailFilter] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get("/bitacora/actividad?limit=500")
      .then(r => setData(r.data))
      .catch(err => toast.error(err?.response?.data?.detail || "Error"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = data.events || [];
    if (emailFilter) list = list.filter(e => (e.email || "").toLowerCase().includes(emailFilter.toLowerCase()));
    return list;
  }, [data, emailFilter]);

  const emails = useMemo(() => {
    const s = new Set();
    (data.events || []).forEach(e => e.email && s.add(e.email));
    return Array.from(s).sort();
  }, [data]);

  if (loading) return <div className="text-center text-sm text-[var(--ink-muted)] py-8">Cargando…</div>;

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="flex bg-white border border-[var(--border)] rounded-md overflow-hidden text-sm">
          <button
            data-testid="filter-all"
            onClick={() => setEmailFilter("")}
            className={`px-3 py-1.5 ${!emailFilter ? "bg-black text-white" : "text-[var(--ink)]"}`}
          >Todos</button>
          {emails.map(e => (
            <button
              key={e}
              data-testid={`filter-email-${e}`}
              onClick={() => setEmailFilter(e)}
              className={`px-3 py-1.5 border-l border-[var(--border)] ${emailFilter === e ? "bg-black text-white" : "text-[var(--ink)]"}`}
            >{e}</button>
          ))}
        </div>
        <span className="text-xs text-[var(--ink-muted)] ml-auto">{filtered.length} eventos</span>
      </div>

      <div className="bg-white border border-[var(--border)] rounded-lg overflow-hidden" data-testid="actividad-table">
        <div className="grid grid-cols-[140px_1fr_260px_120px] text-xs uppercase tracking-wider font-mono-num text-[var(--ink-muted)] bg-[var(--bg)] px-4 py-2.5 border-b border-[var(--border)]">
          <div>Fecha</div>
          <div>Acción</div>
          <div>Usuario</div>
          <div className="text-right">IP</div>
        </div>
        {filtered.map((e, i) => {
          const meta = ACTION_LABEL[e.action] || { l: e.action, icon: Activity, color: "text-[var(--ink-muted)]" };
          const Icon = meta.icon;
          return (
            <div key={i} className="grid grid-cols-[140px_1fr_260px_120px] px-4 py-2.5 text-sm border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--hover)]" data-testid={`activity-row-${i}`}>
              <div className="text-xs font-mono-num text-[var(--ink-muted)]">{formatDT(e.ts)}</div>
              <div className={`inline-flex items-center gap-2 ${meta.color}`}>
                <Icon size={13} />
                <span>{meta.l}</span>
              </div>
              <div className="text-xs truncate">{e.email || "—"}</div>
              <div className="text-right text-xs font-mono-num text-[var(--ink-muted)]">{e.ip || "—"}</div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center text-sm text-[var(--ink-muted)] py-8">Sin actividad registrada.</div>
        )}
      </div>
    </div>
  );
}
